import { useEffect, useRef, useState } from "react";
import type { GenerationJob } from "@lorevok/domain";
import type { Page } from "../types.js";
import { formatCreditCompact, formatCredits } from "../lib/credits.js";
import { Avatar } from "./Avatar.js";
import { CreditIcon } from "./CreditIcon.js";
import { FeedbackModal } from "./FeedbackButton.js";
import { LanguageSelect } from "./LanguageSelect.js";
import { NotificationBell } from "./NotificationBell.js";
import { useT } from "../lib/i18n/store.js";
import { followInApp } from "../lib/links.js";
import { buildPath } from "../lib/route.js";

// Generation-pill statuses the author acts on (each maps to a nav.gen* key).
const GEN_PILL_STATUSES = ["queued", "running", "completed", "failed"];

export function Topbar({
  activePage,
  onNavigate,
  authEnabled = false,
  userEmail = null,
  sessionLoading = false,
  identityLoaded = true,
  userDisplayName = null,
  userAvatarUrl = null,
  balanceCents = null,
  balancePending = false,
  isAdmin = false,
  generationStatus = null,
  generationProgress = 0,
  onOpenGeneration,
  onOpenProfile,
  onSignIn,
  onSignOut,
  showNotifications = false,
  onOpenNovel
}: {
  activePage: Page;
  onNavigate: (page: Page) => void;
  authEnabled?: boolean;
  userEmail?: string | null;
  /**
   * The saved session is still being restored (possibly a token refresh over
   * the network). Neither "Sign in" nor an identity is shown yet — a signed-in
   * author must not see "Sign in" flash first.
   */
  sessionLoading?: boolean;
  /** The author's profile (name + avatar) is known; until then a placeholder shows. */
  identityLoaded?: boolean;
  /** The signed-in author's public display name; shown as the clickable identity. */
  userDisplayName?: string | null;
  /** The signed-in author's avatar URL; shown as a small chip before the name. */
  userAvatarUrl?: string | null;
  balanceCents?: number | null;
  /** The balance's first read is still in flight: hold its chip's place. */
  balancePending?: boolean;
  isAdmin?: boolean;
  /** Status of the author's active/just-finished generation, or null to hide the pill. */
  generationStatus?: GenerationJob["status"] | null;
  generationProgress?: number;
  onOpenGeneration?: () => void;
  /** Opens the signed-in author's own public profile (clicking their name). */
  onOpenProfile?: () => void;
  onSignIn?: () => void;
  onSignOut?: () => void;
  /** Show the notifications bell (signed in, or the keyless local_user path). */
  showNotifications?: boolean;
  /** Opens a novel's detail page from a clicked notification. */
  onOpenNovel?: (novelId: string) => void;
}) {
  const t = useT();
  const [menuOpen, setMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const menuWrapRef = useRef<HTMLDivElement>(null);

  // Dismiss the popup on an outside click or Escape. Only wired while it's open.
  useEffect(() => {
    if (!menuOpen) return;
    function onPointerDown(event: MouseEvent): void {
      if (menuWrapRef.current && !menuWrapRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  function go(page: Page): void {
    setMenuOpen(false);
    onNavigate(page);
  }

  // A menu entry that opens a page: a real link (crawlable, "open in new tab"),
  // in-app on a plain click (lib/links.ts).
  function pageLink(page: Page, label: string, active: boolean) {
    return (
      <a
        href={buildPath(page, null)}
        className={active ? "is-active" : undefined}
        aria-current={activePage === page ? "page" : undefined}
        onClick={(event) => followInApp(event, () => go(page))}
      >
        {label}
      </a>
    );
  }

  // Show a pill only for states the author acts on. A running pill also shows
  // live progress; a terminal one invites them back to publish or retry. The
  // pill stays visible in the bar (not the popup) because it is a time-sensitive
  // status, not one of the buttons we are decluttering.
  const showGenPill =
    Boolean(onOpenGeneration) &&
    generationStatus !== null &&
    GEN_PILL_STATUSES.includes(generationStatus);
  const genPillLabel =
    generationStatus === "running"
      ? t("nav.genRunning", { progress: generationProgress })
      : generationStatus === "queued"
        ? t("nav.genQueued")
        : generationStatus === "completed"
          ? t("nav.genCompleted")
          : generationStatus === "failed"
            ? t("nav.genFailed")
            : "";
  // Phones show just the gist (the full label is the tooltip): the bar is one
  // row, and a long pill used to push it wider than the screen.
  const genPillShort =
    generationStatus === "running"
      ? `${generationProgress ?? 0}%`
      : generationStatus === "completed"
        ? "✓"
        : generationStatus === "failed"
          ? "!"
          : "…";

  const showFeedback = !authEnabled || Boolean(userEmail);

  return (
    <header className="topbar">
      <a className="brand-button" href={buildPath("landing", null)} onClick={(event) => followInApp(event, () => go("landing"))}>
        <svg className="brand-mark" width="24" height="24" viewBox="0 0 32 32" aria-hidden="true">
          <g fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
            <path d="M16 13 C 15 9.2 13.3 7.2 11.4 5.9" />
            <path d="M16 13 L 16 4.7" />
            <path d="M16 13 C 17 9.2 18.7 7.2 20.6 5.9" />
          </g>
          <g fill="currentColor">
            <circle cx="11.1" cy="5.1" r="1.5" />
            <circle cx="16" cy="3.6" r="1.5" />
            <circle cx="20.9" cy="5.1" r="1.5" />
          </g>
          <path d="M15.2 12.9 C 12.3 11.4 8.9 11.15 6 12 L 6 23.3 C 8.9 22.5 12.3 22.75 15.2 24.3 Z" fill="currentColor" />
          <path d="M16.8 12.9 C 19.7 11.4 23.1 11.15 26 12 L 26 23.3 C 23.1 22.5 19.7 22.75 16.8 24.3 Z" fill="currentColor" />
        </svg>
        Lorvok
      </a>

      <div className="topbar-right">
        {showGenPill && (
          <button
            type="button"
            className={`nav-genstatus nav-genstatus-${generationStatus}`}
            onClick={() => onOpenGeneration?.()}
            aria-label={t("nav.viewGeneration")}
            title={genPillLabel}
          >
            <span className="nav-genstatus-dot" aria-hidden="true" />
            <span className="nav-genstatus-label">{genPillLabel}</span>
            <span className="nav-genstatus-short" aria-hidden="true">
              {genPillShort}
            </span>
          </button>
        )}

        {showNotifications && <NotificationBell onOpenNovel={onOpenNovel} />}

        <LanguageSelect />

        {/* A signed-out visitor's way in stays visible in the bar, not only in
            the menu — sign-in is also where a new account is created. */}
        {authEnabled && !sessionLoading && !userEmail && activePage !== "auth" && (
          <button type="button" className="nav-signin" onClick={() => onSignIn?.()}>
            {t("nav.signIn")}
          </button>
        )}

        {/* Signed in: the author and the balance sit in the bar itself. */}
        {authEnabled && userEmail && (
          <button
            type="button"
            className="nav-identity ph-no-capture"
            title={userEmail}
            onClick={() => {
              setMenuOpen(false);
              onOpenProfile?.();
            }}
          >
            {identityLoaded ? (
              <>
                <Avatar
                  name={userDisplayName?.trim() || userEmail}
                  url={userAvatarUrl}
                  size={28}
                  className="nav-identity-avatar"
                />
                <span className="nav-identity-name">{userDisplayName?.trim() || userEmail}</span>
              </>
            ) : (
              // Until /api/me answers: a neutral chip, not the email or a
              // generic name that the real one then replaces.
              <span className="nav-identity-avatar skeleton nav-identity-placeholder" aria-hidden="true" />
            )}
          </button>
        )}
        {/* The balance chip follows the author; it is the way to buy more
            credits (opens /pricing). ph-no-capture: its text and title are the
            balance, which analytics never receive. */}
        {authEnabled && userEmail && balanceCents !== null && (
          <button
            type="button"
            className={activePage === "pricing" ? "nav-balance is-active ph-no-capture" : "nav-balance ph-no-capture"}
            title={t("nav.balanceAction", { amount: formatCredits(balanceCents, t) })}
            aria-label={t("nav.balanceAction", { amount: formatCredits(balanceCents, t) })}
            onClick={() => go("pricing")}
          >
            <CreditIcon size={16} />
            <span className="nav-balance-amount">{formatCreditCompact(balanceCents, t)}</span>
          </button>
        )}
        {authEnabled && userEmail && balanceCents === null && balancePending && (
          // No role: the page's own status line is the loading announcement.
          <span className="nav-balance-skeleton skeleton" aria-hidden="true" />
        )}

        <div className="nav-menu-wrap" ref={menuWrapRef}>
          <button
            type="button"
            className={menuOpen ? "nav-menu-trigger is-open" : "nav-menu-trigger"}
            aria-haspopup="true"
            aria-expanded={menuOpen}
            aria-label={t("nav.menu")}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>

          {menuOpen && (
            <div className="nav-menu">
              {/* Phones: the language picker lives here, not in the bar. */}
              <div className="nav-menu-lang">
                <LanguageSelect />
              </div>
              {pageLink("catalog", t("nav.catalog"), activePage === "catalog")}
              {pageLink("create", t("nav.create"), activePage === "create")}
              {pageLink("pricing", t("footer.pricing"), activePage === "pricing")}
              {showFeedback && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setFeedbackOpen(true);
                  }}
                >
                  {t("nav.feedback")}
                </button>
              )}
              {pageLink(
                "terms",
                t("nav.legal"),
                activePage === "terms" || activePage === "privacy" || activePage === "refund"
              )}
              {authEnabled && (
                <div className="nav-menu-account">
                  {userEmail ? (
                    <>
                      {isAdmin && pageLink("admin", t("nav.admin"), activePage === "admin")}
                      <button
                        type="button"
                        onClick={() => {
                          setMenuOpen(false);
                          onSignOut?.();
                        }}
                      >
                        {t("nav.signOut")}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className={activePage === "auth" ? "is-active" : undefined}
                      onClick={() => {
                        setMenuOpen(false);
                        onSignIn?.();
                      }}
                    >
                      {t("nav.signIn")}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </header>
  );
}
